import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Info } from "lucide-react";

const EVENTS = [
  { key: "mens_singles", label: "Men's Singles" },
  { key: "mens_doubles", label: "Men's Doubles" },
  { key: "womens_singles", label: "Women's Singles" },
  { key: "womens_doubles", label: "Women's Doubles" },
  { key: "mixed_doubles", label: "Mixed Doubles" },
];
const AGES = [
  { key: "u_10", label: "U-10" },
  { key: "u_12", label: "U-12" },
  { key: "u_14", label: "U-14" },
  { key: "u_16", label: "U-16" },
  { key: "u_19", label: "U-19" },
  { key: "19+", label: "19+ (Open)" },
  { key: "35+", label: "35+" },
  { key: "50+", label: "50+" },
  { key: "60+", label: "60+" },
];

function BreakdownPopover() { return null; }

function PolicyModal() {
  const [policy, setPolicy] = useState(null);
  useEffect(() => { api.get("/ranking-policy").then((r) => setPolicy(r.data)).catch(() => {}); }, []);
  const positions = policy ? Object.entries(policy.position_points) : [];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="rankings-info-btn" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-jpa-primary hover:text-jpa-primary-hover">
          <Info className="w-4 h-4" /> How rankings work
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl rounded-sm p-0 overflow-hidden">
        <div className="bg-jpa-ink text-white px-6 py-4">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wider text-white">JPA STATE RANKING POLICY</DialogTitle>
            <DialogDescription className="text-white/70 text-xs uppercase tracking-widest">AIPA points table · Rolling 12-month · JPA-sanctioned only</DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">Formula</div>
            <p className="text-sm mt-1">{policy?.formula}</p>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">Who counts?</div>
            <p className="text-sm mt-1">{policy?.eligibility}</p>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">Points by finish position</div>
            <div className="mt-2 border border-jpa-border">
              <div className="grid grid-cols-4 bg-jpa-surface py-2 px-3 text-[10px] font-bold uppercase tracking-widest">
                <div>Position</div><div className="text-right">Tier 3</div><div className="text-right">Tier 2</div><div className="text-right">Tier 1</div>
              </div>
              {positions.map(([pos, tiers]) => (
                <div key={pos} className="grid grid-cols-4 py-2 px-3 text-sm border-t border-jpa-border">
                  <div>{policy?.position_labels?.[pos] || pos}</div>
                  <div className="text-right font-bold">{tiers[3]}</div>
                  <div className="text-right">{tiers[2]}</div>
                  <div className="text-right text-jpa-muted">{tiers[1]}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-jpa-muted">
            <div><b>Doubles / Mixed:</b> {policy?.doubles_split}</div>
            <div><b>Tier:</b> {policy?.tier_note}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RankingRow({ r, i }) {
  return (
    <div data-testid={`ranking-row-${r.id}`} className={`grid grid-cols-12 items-center py-2 md:py-3 px-3 md:px-4 hover:bg-jpa-surface transition-colors ${i % 2 === 0 ? "bg-white" : "bg-jpa-surface/50"}`}>
      <Link to={`/players/${r.player_id}`} className="hidden md:block col-span-1 font-display text-2xl text-jpa-primary">{r.rank}</Link>
      <Link to={`/players/${r.player_id}`} className="hidden md:flex col-span-5 items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-sm overflow-hidden bg-jpa-surface flex-shrink-0 flex items-center justify-center">
          {r.player_image ? <img src={r.player_image} alt="" className="w-full h-full object-cover" /> : (
            <span data-testid={`ranking-photo-pending-${r.id}`} className="text-[8px] font-bold uppercase tracking-widest text-jpa-muted text-center px-1 leading-tight">Photo<br/>Pending</span>
          )}
        </div>
        <div className="min-w-0">
          <div className="font-bold uppercase text-sm truncate">{r.player_name}</div>
          {r.partner_name && <div className="text-[10px] text-jpa-muted truncate">Partner: {r.partner_name}</div>}
        </div>
      </Link>
      <Link to={`/players/${r.player_id}`} className="hidden md:block col-span-2 text-sm truncate">{r.district}</Link>
      <div className="hidden md:block col-span-2 text-right font-bold text-jpa-ink">{r.points}</div>
      <div className="hidden md:block col-span-2 text-right text-xs text-jpa-muted">{r.wins}-{r.losses}</div>

      <Link to={`/players/${r.player_id}`} className="md:hidden col-span-2 font-display text-xl text-jpa-primary">#{r.rank}</Link>
      <Link to={`/players/${r.player_id}`} className="md:hidden col-span-7 flex items-center gap-2 min-w-0">
        <div className="w-8 h-8 rounded-sm overflow-hidden bg-jpa-surface flex-shrink-0 flex items-center justify-center">
          {r.player_image ? <img src={r.player_image} alt="" className="w-full h-full object-cover" /> : (
            <span className="text-[7px] font-bold uppercase tracking-widest text-jpa-muted text-center px-0.5 leading-tight">Photo<br/>Pending</span>
          )}
        </div>
        <div className="min-w-0">
          <div className="font-bold uppercase text-xs truncate">{r.player_name}</div>
          <div className="text-[10px] text-jpa-muted truncate">{r.partner_name ? `↳ ${r.partner_name}` : r.district}</div>
        </div>
      </Link>
      <div className="md:hidden col-span-3 text-right font-bold text-jpa-ink text-sm">{r.points}</div>
    </div>
  );
}

function RankingsTable({ rows }) {
  if (rows.length === 0) return <div className="py-16 text-center text-jpa-muted">No ranked players in this category yet. Rankings will appear once tournament results are recorded.</div>;
  return (
    <div className="border border-jpa-border rounded-sm overflow-hidden">
      <div className="hidden md:grid grid-cols-12 bg-jpa-ink text-white py-3 px-4 text-[10px] font-bold uppercase tracking-widest">
        <div className="col-span-1">Rank</div>
        <div className="col-span-5">Player</div>
        <div className="col-span-2">District</div>
        <div className="col-span-2 text-right">Points</div>
        <div className="col-span-2 text-right">W-L</div>
      </div>
      <div className="grid md:hidden grid-cols-12 bg-jpa-ink text-white py-2 px-3 text-[10px] font-bold uppercase tracking-widest">
        <div className="col-span-2">Rank</div>
        <div className="col-span-7">Player</div>
        <div className="col-span-3 text-right">Pts</div>
      </div>
      {rows.map((r, i) => <RankingRow key={r.id} r={r} i={i} />)}
    </div>
  );
}

export default function RankingsPage() {
  const [eventCat, setEventCat] = useState("mens_singles");
  const [age, setAge] = useState("19+");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const TOP_N = 8;

  useEffect(() => {
    setLoading(true);
    setShowAll(false);
    const params = new URLSearchParams({ event_category: eventCat, age, limit: "100" });
    api.get(`/rankings?${params}`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [eventCat, age]);

  const visibleRows = useMemo(() => (showAll ? rows : rows.slice(0, TOP_N)), [rows, showAll]);
  const hiddenCount = Math.max(0, rows.length - TOP_N);

  const scopeLabel = useMemo(() => `${EVENTS.find((e) => e.key === eventCat)?.label} · ${AGES.find((a) => a.key === age)?.label}`, [eventCat, age]);

  return (
    <div data-testid="rankings-page">
      <PageHero eyebrow="Live standings" title="STATE RANKINGS"
        subtitle="JPA-sanctioned events only · Rolling 12-month · Auto-computed after every tournament result."
        image="https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?w=1800" />

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="font-display text-2xl tracking-wider">{scopeLabel}</div>
          <PolicyModal />
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary mb-1">Event category</div>
            <Select value={eventCat} onValueChange={setEventCat}>
              <SelectTrigger data-testid="rankings-event-select" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{EVENTS.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary mb-1">Age category</div>
            <Select value={age} onValueChange={setAge}>
              <SelectTrigger data-testid="rankings-age-select" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{AGES.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-jpa-muted">Showing <b>{visibleRows.length}</b> of <b>{rows.length}</b> ranked player{rows.length === 1 ? "" : "s"}</div>
          </div>
        </div>

        <div className="mt-8">
          {loading ? <div className="py-16 text-center text-jpa-muted">Loading rankings…</div> : (
            <>
              <RankingsTable rows={visibleRows} />
              {hiddenCount > 0 && (
                <div className="mt-4 text-center">
                  <button data-testid="rankings-show-more" onClick={() => setShowAll(!showAll)}
                    className="inline-flex items-center gap-2 px-6 h-11 rounded-sm border border-jpa-primary text-jpa-primary hover:bg-jpa-primary hover:text-white text-xs font-bold uppercase tracking-widest transition-colors">
                    {showAll ? `Show top ${TOP_N} only` : `Show all ${rows.length} rankings (+${hiddenCount} more)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
